import { useEffect, useMemo, useState } from 'react';
import { Alert, Card, Space, Button, Typography, message, Select } from 'antd';
import { DownloadOutlined, FilePdfOutlined } from '@ant-design/icons';
import dermatoApi from '../../api/dermatoApi';
import type { LesionFull } from '../../api/dermatoApi';
import LanguageSelector from '../../components/common/LanguageSelector';
import DocumentLanguageSelector from '../../components/common/DocumentLanguageSelector';
import { useDocumentLanguage } from '../../hooks/useDocumentLanguage';
import { DOCUMENT_LANGUAGE_HINTS, DOCUMENT_LANGUAGE_LABELS } from '../../hooks/useLanguage';
import {
  COMMISSION_NOT_RECORDED_TOOLTIP,
  DOCUMENT_FALLBACK_MESSAGE,
  LESION_REQUIRED_TOOLTIP,
  MEDICAL_DOCUMENT_LABELS,
  downloadBlob,
  downloadMedicalDocument,
  type MedicalDocumentKind,
} from './medicalDocuments';

const { Text } = Typography;

interface Props {
  formId: string | null;
  /** Leziunile formularului — dacă nu sunt primite, butoanele de scrisoare rămân dezactivate. */
  lesions?: LesionFull[];
  /** Leziunea selectată în wizard (mecanismul existent, reutilizat). */
  selectedLesionId?: string | null;
  /** Setează leziunea selectată în wizard. */
  onSelectLesion?: (id: string | null) => void;
}

const FILENAME_BY_KIND: Record<'anamnesis' | 'full', string> = {
  anamnesis: 'HEPASKIN_Anamneza',
  full: 'HEPASKIN_Complet',
};

type LoadingKind = 'anamnesis' | 'full' | MedicalDocumentKind;

/** Mesaj de rezervă, în română, în cazul în care backendul nu trimite unul. */
const FALLBACK_MESSAGE = DOCUMENT_FALLBACK_MESSAGE;

/**
 * Export PDF (anamneză / complet) + documentele medicale (§1.2.6).
 *
 * §1.0c.7 — limba documentului e decuplată de limba meniului:
 * `useDocumentLanguage()` (nu `useLanguage()`) controlează limba documentului,
 * iar `LanguageSelector` rămâne selectorul de limă a meniului.
 */
const ExportActions = ({ formId, lesions, selectedLesionId, onSelectLesion }: Props) => {
  const { language } = useDocumentLanguage();
  const [loadingKind, setLoadingKind] = useState<LoadingKind | null>(null);
  const [fallbackMessage, setFallbackMessage] = useState<string | null>(null);
  const [commissionRecorded, setCommissionRecorded] = useState(false);
  const [plansLoaded, setPlansLoaded] = useState(false);

  /**
   * Leziunea folosită pentru documente: cea selectată în wizard; dacă formularul
   * are o singură leziune, aceea e folosită automat.
   */
  const activeLesionId = useMemo(() => {
    if (selectedLesionId) return selectedLesionId;
    if (lesions && lesions.length === 1) return lesions[0].id;
    return null;
  }, [lesions, selectedLesionId]);

  /**
   * Decizia comisiei oncologice se detectează din `TreatmentPlan.commissionOutcomeSummary`
   * (§1.3 — câmpul `commissionDecision` a fost eliminat din model).
   */
  useEffect(() => {
    let cancelled = false;
    if (!activeLesionId) {
      setCommissionRecorded(false);
      setPlansLoaded(false);
      return;
    }
    setPlansLoaded(false);
    void dermatoApi.treatments
      .getByLesion(activeLesionId)
      .then((plans) => {
        if (cancelled) return;
        const recorded = Array.isArray(plans)
          ? plans.some((p) => Boolean(p.commissionOutcomeSummary?.trim()))
          : false;
        setCommissionRecorded(recorded);
        setPlansLoaded(true);
      })
      .catch(() => {
        if (cancelled) return;
        setCommissionRecorded(false);
        setPlansLoaded(true);
      });
    return () => {
      cancelled = true;
    };
  }, [activeLesionId]);

  if (!formId) return null;

  const handleDownload = async (kind: 'anamnesis' | 'full') => {
    try {
      setLoadingKind(kind);
      setFallbackMessage(null);
      const result =
        kind === 'anamnesis'
          ? await dermatoApi.downloadAnamnesisPdfWithMeta(formId, language)
          : await dermatoApi.downloadFullPdfWithMeta(formId, language);

      // §1.0c.8 — numele fișierului folosește limba REZOLVATĂ, nu cea cerută.
      const effectiveLanguage = result.meta.resolvedLanguage ?? language;
      const dateStr = new Date().toISOString().split('T')[0];
      const filename = `${FILENAME_BY_KIND[kind]}_${DOCUMENT_LANGUAGE_LABELS[effectiveLanguage].short}_${dateStr}.pdf`;
      downloadBlob(result.blob, filename);

      if (result.meta.fallback) {
        // Mesajul vine de la backend; rezervă în română dacă nu e trimis.
        setFallbackMessage(result.meta.message ?? FALLBACK_MESSAGE);
      }

      message.success(
        `PDF ${kind === 'full' ? 'complet' : 'anamneză'} descărcat (${DOCUMENT_LANGUAGE_LABELS[effectiveLanguage].native})`,
      );
    } catch (e: any) {
      message.error(e?.response?.data?.message ?? 'Eroare la descărcarea PDF-ului');
    } finally {
      setLoadingKind(null);
    }
  };

  /** Descarcă unul dintre cele 3 documente medicale (§1.2.6). */
  const handleMedicalDocument = async (kind: MedicalDocumentKind) => {
    try {
      setLoadingKind(kind);
      setFallbackMessage(null);
      const result = await downloadMedicalDocument(kind, {
        formId,
        lang: language,
        lesionId: activeLesionId ?? undefined,
      });

      if (result.meta.fallback) {
        setFallbackMessage(result.meta.message ?? FALLBACK_MESSAGE);
      }

      message.success(`${MEDICAL_DOCUMENT_LABELS[kind]} descărcată.`);
    } catch (e: any) {
      message.error(
        e?.response?.data?.message ?? `Eroare la descărcarea documentului „${MEDICAL_DOCUMENT_LABELS[kind]}”`,
      );
    } finally {
      setLoadingKind(null);
    }
  };

  const needsLesion = !activeLesionId;
  const commissionDisabled = needsLesion || !plansLoaded || !commissionRecorded;
  // Scrisoarea de screening se generează doar pentru finalizarea fără leziuni.
  const hasNoLesions = !lesions || lesions.length === 0;

  return (
    <Card
      size="small"
      style={{ marginBottom: 12, borderColor: '#91caff' }}
      data-testid="dermato-export"
      title={
        <Space>
          <FilePdfOutlined style={{ color: '#cf1322' }} />
          <span>Export PDF — {DOCUMENT_LANGUAGE_LABELS[language].native}</span>
        </Space>
      }
    >
      {fallbackMessage && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 12 }}
          message="Limba documentului a fost ajustată"
          description={fallbackMessage}
          data-testid="export-fallback-alert"
        />
      )}

      <Space wrap>
        <Button
          icon={<DownloadOutlined />}
          onClick={() => void handleDownload('anamnesis')}
          loading={loadingKind === 'anamnesis'}
        >
          Anamneză (secțiunile I-VI)
        </Button>
        <Button
          type="primary"
          icon={<DownloadOutlined />}
          onClick={() => void handleDownload('full')}
          loading={loadingKind === 'full'}
        >
          Complet (secțiunile I-XIII)
        </Button>

        <Text type="secondary" style={{ fontSize: 12 }}>
          Limba meniului:
        </Text>
        <LanguageSelector variant="inline" />

        <DocumentLanguageSelector id="dermato-document-language" />
      </Space>

      {/* §1.2.6 — cele 3 documente medicale */}
      <div style={{ marginTop: 12 }}>
        <Space wrap align="center">
          <Text strong style={{ fontSize: 12 }}>
            Documente medicale:
          </Text>

          {lesions && lesions.length > 1 && (
            <Select
              size="small"
              id="dermato-medical-document-lesion"
              aria-label="Leziune pentru documentele medicale"
              value={activeLesionId ?? undefined}
              onChange={(value) => onSelectLesion?.(value)}
              placeholder="Alege leziunea"
              options={lesions.map((l) => ({
                value: l.id,
                label: `${l.clinicalCode} — ${l.location}`,
              }))}
              style={{ minWidth: 200 }}
            />
          )}

          <span title={needsLesion ? LESION_REQUIRED_TOOLTIP : undefined}>
            <Button
              icon={<DownloadOutlined />}
              onClick={() => void handleMedicalDocument('referral-dermato')}
              loading={loadingKind === 'referral-dermato'}
              disabled={needsLesion}
            >
              {MEDICAL_DOCUMENT_LABELS['referral-dermato']}
            </Button>
          </span>

          <span title={needsLesion ? LESION_REQUIRED_TOOLTIP : undefined}>
            <Button
              icon={<DownloadOutlined />}
              onClick={() => void handleMedicalDocument('referral-surgery')}
              loading={loadingKind === 'referral-surgery'}
              disabled={needsLesion}
            >
              {MEDICAL_DOCUMENT_LABELS['referral-surgery']}
            </Button>
          </span>

          <span
            title={
              needsLesion
                ? LESION_REQUIRED_TOOLTIP
                : commissionRecorded
                  ? undefined
                  : COMMISSION_NOT_RECORDED_TOOLTIP
            }
          >
            <Button
              icon={<DownloadOutlined />}
              onClick={() => void handleMedicalDocument('commission-sheet')}
              loading={loadingKind === 'commission-sheet'}
              disabled={commissionDisabled}
            >
              {MEDICAL_DOCUMENT_LABELS['commission-sheet']}
            </Button>
          </span>

          <span
            title={hasNoLesions ? undefined : 'Disponibil doar pentru un screening finalizat fără leziuni'}
          >
            <Button
              icon={<DownloadOutlined />}
              onClick={() => void handleMedicalDocument('screening-letter')}
              loading={loadingKind === 'screening-letter'}
              disabled={!hasNoLesions}
            >
              {MEDICAL_DOCUMENT_LABELS['screening-letter']}
            </Button>
          </span>
        </Space>
      </div>

      <div style={{ marginTop: 8 }}>
        <Text type="secondary" style={{ fontSize: 12 }}>
          {DOCUMENT_LANGUAGE_HINTS[language] ?? 'Limba documentului PDF (RO / EN / SRB).'}
        </Text>
      </div>
    </Card>
  );
};

export default ExportActions;
